/* eslint-disable @typescript-eslint/no-empty-function */
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { dateCalender } from '@g99-product-web-ui/shared-util-lib';
import { Calendar } from 'primeng/calendar';

@Component({
  selector: 'lib-date-input',
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DateInputComponent
  implements ControlValueAccessor, OnInit, OnChanges, AfterViewInit
{
  @Input() public placeholder = '';
  @Input() public label = '';
  @Input() public labelout = '';
  @Input() public inputId = '';
  @Input() public name = '';
  @Input() public isInvalid = false;
  @Input() public height = '40px';
  @Input() public errorMessage: { [key: string]: string } = {};
  @Input() public formControls!: AbstractControl | null;
  @Input() public minDate: Date | null = null;
  @Input() public maxDate!: Date;
  @Input() public selectionMode: 'single' | 'multiple' | 'range' = 'single';
  @Input() public showTimeInCalender = false;
  @Input() public showPlaceholderIcon = false;
  @Input() public iconPositionAtEnd = false;
  @Input() public placeholderIcon = dateCalender;
  @Input() public dropdownHeight = '40px';
  @Input() public dropdownWidth = '100%';
  @Input() public border = '1px solid #eaecf0';
  @Input() public appendTo: string | null = null;
  @Output() public changeEvent = new EventEmitter<Date | Date[] | null>();

  @ViewChild(Calendar) private _calendar?: Calendar;

  public value!: Date | Date[] | null;
  protected effectiveMinDate: Date | null = null;
  protected effectiveMaxDate: Date | null = null;

  public onChange: (value: Date | Date[]) => void = () => {};
  public onTouched: () => void = () => {};

  ngOnInit(): void {
    this._updateEffectiveLimits();
  }

  ngAfterViewInit(): void {
    // Workaround for PrimeNG 19.1.4 bug: Calendar declares `window` as a private
    // field but never initialises it, so bindDocumentResizeListener crashes with
    // "Cannot read properties of undefined (reading 'addEventListener')".
    // We cast through `unknown` (not `any`) to safely assign the missing value.
    if (this._calendar) {
      const cal = this._calendar as unknown as {
        window: Window & typeof globalThis;
      };
      if (!cal.window) {
        cal.window = window;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['minDate'] || changes['maxDate']) {
      this._updateEffectiveLimits();
    }
  }

  // ControlValueAccessor implementation
  public writeValue(value: Date | Date[] | string | string[]): void {
    if (Array.isArray(value)) {
      this.value = value.map((v) => this._parseDate(v) as Date);
    } else {
      this.value = this._parseDate(value) as Date;
    }

    // Update minDate for range selection based on first selected date
    if (
      this.selectionMode === 'range' &&
      Array.isArray(this.value) &&
      this.value[0]
    ) {
      this.effectiveMinDate = this.value[0];
    } else if (this.selectionMode === 'range' && !this.minDate) {
      this.effectiveMinDate = null;
    }
  }

  public registerOnChange(fn: (value: Date | Date[]) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public onInputChange(date: Date | Date[]): void {
    this.value = date;

    // For range mode, dynamically update minDate based on first selection
    if (this.selectionMode === 'range' && Array.isArray(date)) {
      if (date[0] && !date[1]) {
        // First date selected, set it as minDate for second selection
        this.effectiveMinDate = date[0];
      } else if (!date[0] && !date[1]) {
        // Range cleared, reset minDate
        this.effectiveMinDate = this.minDate;
      }
    }

    this.onChange(this.value);
    this.changeEvent.emit(this.value);
    this.onTouched();
  }

  public getErrorMessage(): string | null {
    if (
      this.formControls &&
      this.formControls.invalid &&
      (this.formControls.dirty || this.formControls.touched)
    ) {
      for (const errorKey in this.formControls.errors) {
        if (this.errorMessage[errorKey]) {
          return this.errorMessage[errorKey];
        }
      }
    }
    return '';
  }

  private _updateEffectiveLimits(): void {
    // Set default minDate only for single selection mode if not provided
    if (this.selectionMode === 'single' && !this.minDate) {
      const pastDate = new Date();
      pastDate.setFullYear(pastDate.getFullYear() - 100);
      this.effectiveMinDate = pastDate;
    } else {
      this.effectiveMinDate = this._parseDate(this.minDate);
    }

    this.effectiveMaxDate = this._parseDate(this.maxDate);
  }

  private _parseDate(
    date: string | Date | string[] | Date[] | null
  ): Date | null {
    if (!date) {
      return null;
    }
    if (date instanceof Date) {
      return date;
    }
    const dateToParse = Array.isArray(date) ? date[0] : date;
    const parsed = new Date(dateToParse);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
}
